'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { gql, useLazyQuery, useMutation } from '@apollo/client'
import {
  queuePendingRecipeRatingPrompt,
  type PendingRecipeRatingPrompt,
} from '@/lib/pendingRecipeRatingPrompt'
import type { RecipeRatePromptRecipe } from '@/components/recipe/RecipeRatePromptModal'

const SHOULD_PROMPT_RECIPE_RATING = gql`
  query ShouldPromptRecipeRating($recipe_id: Int!) {
    shouldPromptRecipeRating(recipe_id: $recipe_id)
  }
`

const DISMISS_RECIPE_RATING_PROMPT = gql`
  mutation DismissRecipeRatingPrompt($recipe_id: Int!) {
    dismissRecipeRatingPrompt(recipe_id: $recipe_id)
  }
`

const COOK_MODE_MIN_MS = 3 * 60 * 1000

/**
 * In-app rating prompt: show after a meaningful Cook Mode session ends
 * (toggle off on-page, or navigate away). Bookmark is not a same-session trigger.
 * Skip/X persist a 30-day snooze on the server.
 */
export function useRecipeRatingPrompt({
  recipeId,
  enabled,
  recipeSnapshot,
}: {
  recipeId?: number | null
  /** false for guests, own recipes, etc. */
  enabled: boolean
  /** Needed to re-show the modal after navigating away from the recipe page. */
  recipeSnapshot?: RecipeRatePromptRecipe | null
}) {
  const [open, setOpen] = useState(false)
  const cookStartedAtRef = useRef<number | null>(null)
  const promptingRef = useRef(false)
  const snapshotRef = useRef(recipeSnapshot)
  const openRef = useRef(false)
  snapshotRef.current = recipeSnapshot
  openRef.current = open

  const [checkShouldPrompt] = useLazyQuery(SHOULD_PROMPT_RECIPE_RATING, {
    fetchPolicy: 'no-cache',
  })
  const [dismissPrompt] = useMutation(DISMISS_RECIPE_RATING_PROMPT)

  const maybePrompt = useCallback(async () => {
    if (!enabled || !recipeId || promptingRef.current || open) return

    promptingRef.current = true
    try {
      const res = await checkShouldPrompt({ variables: { recipe_id: recipeId } })
      if (res.data?.shouldPromptRecipeRating) {
        setOpen(true)
      }
    } catch {
      // Fail closed — never block the recipe page on prompt errors
    } finally {
      promptingRef.current = false
    }
  }, [enabled, recipeId, open, checkShouldPrompt])

  const markCookModeStarted = useCallback(() => {
    cookStartedAtRef.current = Date.now()
  }, [])

  const onCookModeEnded = useCallback(() => {
    const started = cookStartedAtRef.current
    cookStartedAtRef.current = null
    if (!started) return
    if (Date.now() - started < COOK_MODE_MIN_MS) return
    void maybePrompt()
  }, [maybePrompt])

  // Leaving the recipe page with a long Cook Mode session (or an open prompt)
  // → queue for layout host so the modal survives navigation.
  useEffect(() => {
    return () => {
      const snap = snapshotRef.current
      if (!enabled || !recipeId || !snap?.slug) return

      const started = cookStartedAtRef.current
      const longCookSession =
        !!started && Date.now() - started >= COOK_MODE_MIN_MS
      if (!longCookSession && !openRef.current) return

      queuePendingRecipeRatingPrompt({
        ...snap,
        recipe_id: recipeId,
      })
    }
  }, [enabled, recipeId])

  const skip = useCallback(async () => {
    setOpen(false)
    if (!recipeId) return
    try {
      await dismissPrompt({ variables: { recipe_id: recipeId } })
    } catch {
      // local close still happens
    }
  }, [recipeId, dismissPrompt])

  const dismissWithoutSnooze = useCallback(() => {
    setOpen(false)
  }, [])

  const rate = useCallback(() => {
    setOpen(false)
    requestAnimationFrame(() => {
      document.getElementById('recipe-reviews')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }, [])

  useEffect(() => {
    setOpen(false)
    cookStartedAtRef.current = null
  }, [recipeId])

  return {
    open,
    markCookModeStarted,
    onCookModeEnded,
    skip,
    rate,
    dismissWithoutSnooze,
  }
}

// Re-export type for callers that queue snapshots
export type { PendingRecipeRatingPrompt }
